import { Box, Divider, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { VirtualItem } from "@tanstack/react-virtual";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { WorkspacesContext } from "../../../contexts/WorkspacesContext";
import useBackwardsCompatible from "../../../hooks/useBackwardsCompatible";
import useDashboards from "../../../hooks/useDashboards";
import usePaginator from "../../../hooks/usePaginator";
import useWorkspaces from "../../../hooks/useWorkspaces";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard, Workspace } from "../../../types/files";
import { collectionKey } from "../../organisms/dashboards/DashboardsView";
import { FallbackLoading } from "../../templates/loader";
import NoDashboardsFoundView from "../dashboards-empty/NoDashboardsFoundView";
import DashboardsTableFilter from "../dashboards-table/DashboardsTableFilter";
import DashboardsTableHeader from "../dashboards-table/DashboardsTableHeader";
import DashboardsTableRow from "../dashboards-table/DashboardsTableRow";

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    scrollable: {
        width: '100%',
        height: '100%',
        marginBottom: '16px !important',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    headers: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    //
    table: {
        width: '100%',
        height: 'calc(100vh - 180px)',
        marginLeft: 12,
    },
    header: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'auto',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        ...scrollbarStyle,
    },
    rows: {
        position: 'relative',
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    row: {
        position: 'absolute',
        top: 0,
        left: 0,
        minWidth: '100%',
        width: 'fit-content',
        height: 'auto',
        borderTop: `1px solid ${theme.colors.neutral['400']}`,
        background: `#fff`,
        "&:hover": {
            background: `rgb(246, 251, 251, 0.8)`,
        },
    },
    section: {
        width: '100%',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#3ABDC6',
        "&::before, &::after": {
            borderTop: 'thin solid #3ABDC6',
        }
    },
    loadingText: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 700,
        color: theme.colors.neutral['900'],
    },
    loadingTextSub: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        color: '#9e9e9e',
    },
    loading: {
        width: '100%',
        height: '100%',
        color: theme.palette.primary.main,
    },
    sharedTitle: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: '600',
        color: theme.colors.neutral['700'],
    },
}));

export const TableHeaders = ['Name', 'Fit score', 'Stage', 'Sectors', 'Location', 'Deal source', 'Request Intro', '', 'Added', ''];

const SharedByAll: React.FC<{
    workspace: Workspace,
    sectors: string[],
    stages: string[],
    search?: string,
    withFilter?: boolean,
    onFilter?: ({ tags, stage } : { tags: string[],  stage: string[], }) => void,
    onCount?: (count: number) => void,
}> = ({ workspace, sectors, stages, search, withFilter, onFilter, onCount }) => {
    const classes = useStyles();
    const { workspaces, workspaceScores, setWorkspaceScores } = useContext(WorkspacesContext);
    const { isPublicView, externalDashboards } = useContext(DashboardsContext);
    const { filterDashboardList } = useDashboards();
    const { dashboardWorkspaces } = useBackwardsCompatible();
    const { refreshScores, computeWorkspaceScores } = useWorkspaces();

    const [loading, setLoading] = useState<boolean>(true);
    const [scoring, setScoring] = useState<boolean>(false);
    const [dashboardArray, setDashboardArray] = useState<Dashboard[]|null>(null);
    const [sortBy, setSortBy] = useState<string>('Added');
    const timerRefA = useRef<string | number | NodeJS.Timeout | undefined>(undefined);
    const timerRefB = useRef<string | number | NodeJS.Timeout | undefined>(undefined);
    const virtualRowsRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    const syncScroll = (e?: Event) => {
        e?.preventDefault();

        if (headerRef.current && virtualRowsRef.current)
            headerRef.current.scrollLeft = virtualRowsRef.current.scrollLeft;
    };

    const extSharedWorkspace = useMemo(() =>
        !workspaces?.find(ws => ws?.id === workspace?.id) && workspace?.isPublic && workspace?.isShared
    , [workspace, workspaces]);

    const getMembership = useCallback((dashboard: Dashboard) =>
        dashboard?.workspaceMemberships?.find(wsMem => wsMem.workspaceId === workspace?.id)
    , [workspace?.id]);

    const handleRefresh = useCallback(() => {
        if (!!workspace)
            refreshScores([workspace], externalDashboards, () => setScoring(true))
    }, [workspace, refreshScores, externalDashboards]);

    const dashboardScoreMap: Map<Dashboard, number> = useMemo(() => new Map(
        dashboardArray?.filter(dashboard => dashboardWorkspaces(dashboard)?.includes(workspace!.id))
            ?.map(dashboard => [dashboard,
                workspaceScores.find(({workspaceId}) => workspaceId === workspace?.id)?.scores
                    ?.find(({dashboardId}) => dashboardId === dashboard.id)?.score || 0
            ]) ?? []
    // eslint-disable-next-line
    ), [dashboardArray, workspace, workspaceScores]);

    const sortedDashboards = useMemo(() => {
        if (sortBy === 'Fit score')
            return Array.from(dashboardScoreMap).sort((a, b) => b[1] - a[1]).map(entry => entry[0]);

        return [...dashboardArray ?? []].sort((prev: Dashboard, next: Dashboard) => {
            const prevDate = getMembership(prev)?.updatedAt || prev.createdAt;
            const nextDate = getMembership(next)?.updatedAt || next.createdAt;

            return nextDate.localeCompare(prevDate);
        });
    // eslint-disable-next-line
    }, [dashboardArray, sortBy]);

    const filteredDashboards = useMemo(() => {
        let filtered: Dashboard[] = sortedDashboards;

        if (!!sectors.length){
            filtered = filtered.filter(dashboard =>
                sectors.every(key => (dashboard.tags || []).includes(key))
            );
        }

        if (!!stages.length){
            filtered = filtered.filter(dashboard => {
                const stage = dashboard.investmentStage || '';

                return stages.some(key => key === stage);
            });
        }

        return filtered;
    }, [sectors, sortedDashboards, stages]);

    const { allRows, rowVirtualizer, hasNextPage } = usePaginator({
        key: 'shared-by-all-216',
        source: filteredDashboards,
        limit: 20,
        itemHeight: 60,
        virtualRowsRef,
    });

    useEffect(() => {
        const timeout = setTimeout(() => {
            clearTimeout(timeout);
            if (!headerRef.current || !virtualRowsRef.current) return;

            virtualRowsRef.current.addEventListener("scroll", syncScroll);
            headerRef.current.addEventListener("wheel", syncScroll, { passive: false });
            headerRef.current.addEventListener("touchmove", syncScroll, { passive: false });
        }, 1000);

        return () => {
            clearTimeout(timeout);
            if (!headerRef.current || !virtualRowsRef.current) return;

            // eslint-disable-next-line
            virtualRowsRef?.current?.removeEventListener("scroll", syncScroll);
            headerRef?.current?.removeEventListener("wheel", syncScroll);
            // eslint-disable-next-line
            headerRef?.current?.removeEventListener("touchmove", syncScroll);
        };
    }, []);

    useEffect(() => {
        timerRefA.current = setTimeout(() => {
            setDashboardArray(filterDashboardList(search || '', workspace?.id));
            clearTimeout(timerRefA.current);
        }, 500);

        return () => {
            clearTimeout(timerRefA.current);
        }
    }, [filterDashboardList, workspace?.id, search]);

    useEffect(() => {
        setLoading(!!timerRefA.current && !dashboardArray && !search && !scoring);
    }, [dashboardArray, search, scoring]);

    useEffect(() => {
        onCount?.(filteredDashboards.length);
    // eslint-disable-next-line
    }, [filteredDashboards]);

    useEffect(() => {
        if (!!workspace && scoring) {
            timerRefB.current = setTimeout(() => {
                clearInterval(timerRefB.current);
                computeWorkspaceScores([workspace]).then((updatedWorkspaceScores) => {
                    setWorkspaceScores(updatedWorkspaceScores);
                }).finally(() => setScoring(false));
            }, 3000);
        }

        return () => {
            clearTimeout(timerRefB.current);
        }
    // eslint-disable-next-line
    }, [scoring]);

    if (!workspace)
        return (<></>);

    return (<>
         {loading ? (<FallbackLoading />) : (<>
            {isPublicView && (<>
                <Typography className={classes.sharedTitle} pl={1} py={1}>
                    {`Deals shared:`}
                </Typography>
                <Divider sx={{ width: '100%' }} />
            </>)}
            <Stack className={classes.table}>
                {loading ? (<FallbackLoading />) : (<>
                    {withFilter && ( <DashboardsTableFilter count={filteredDashboards.length} onFilter={onFilter} /> )}
                        <Stack alignItems="flex-start" justifyContent="flex-start" height="100%">
                            <Box width="100%" height="fit-content" overflow="hidden">
                                <Box className={classes.header} ref={headerRef}>
                                    <DashboardsTableHeader
                                        columns={TableHeaders}
                                        columnSorted={sortBy}
                                        onColumnSort={setSortBy}
                                        onRefreshScores={() => handleRefresh()}
                                        actions={['Remove from workspace']}
                                        xAdjust={80}
                                        noStatus multiAction reducedWidth />
                                </Box>
                            </Box>
                            {(!!filteredDashboards.length) ? (<>
                                <Box width="100%" height="100%" overflow="hidden">
                                    <Box className={classes.rows} ref={virtualRowsRef}
                                        sx={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                                        {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => (
                                            <Box className={classes.row}
                                                sx={{  height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)`, }}
                                                key={'shared-by-all-372-' + virtualRow.index} >
                                                {(virtualRow.index > allRows.length - 1) && hasNextPage
                                                    ? (<FallbackLoading />)
                                                    : (<DashboardsTableRow
                                                            dashboard={allRows[virtualRow.index]}
                                                            workspace={workspace ?? undefined}
                                                            columns={TableHeaders}
                                                            collectionKey={collectionKey}
                                                            timestamp={getMembership(allRows[virtualRow.index])?.updatedAt}
                                                            refreshingScore={scoring}
                                                            overview={extSharedWorkspace}
                                                            noStatus isPublic reducedWidth />)}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </>) : (<NoDashboardsFoundView noCompaniesYet={!search && !dashboardArray?.length} noCompaniesFound />)}
                    </Stack>
                </>)}
            </Stack>
        </>)}
    </>);
}

export default SharedByAll;